import {Injectable} from "@angular/core";
import {CompteApi} from "@domain/admin/parametre/api/compte-api";
import {BehaviorSubject,Observable} from "rxjs";
import {ApiInformation,ApiRestService} from "@services/admin/parametre/api/api-rest.service";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {Router} from "@angular/router";
import {filter} from "rxjs/operators";

/**
 * Service des détails d'un compte API REST
 */
@Injectable()
export class ApiRestDetailsService {
    /** Code d'erreur pour l'envoi du mail ou la génération de la clé */
    public static readonly ERROR_CLEF = 905;

    /** Compte API concerné */
    private _compteApi: BehaviorSubject<CompteApi> = new BehaviorSubject<CompteApi>(null);
    compteApi$: Observable<CompteApi> = this._compteApi.asObservable();
    get compteApi(): CompteApi { return this._compteApi.value; }

    /** Informations sur l'API */
    private _apiInformation: BehaviorSubject<ApiInformation> = new BehaviorSubject<ApiInformation>(null);
    apiInformation$: Observable<ApiInformation> = this._apiInformation.asObservable();
    get apiInformation(): ApiInformation { return this._apiInformation.value; }

    /**
     * Constructeur
     */
    constructor(
        private router: Router,
        private apiRestService: ApiRestService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private confirmService: ConfirmService) {}

    /**
     * Chargement d'un compte API
     *
     * @param idCompte ID du compte API
     */
    loadCompteApi(idCompte: number) {
        //Si l'ID est vide, on est en création
        if (!idCompte) {
            //Initialisation du compte
            this._compteApi.next(new CompteApi({ actif: true,comptePersonnel: false } as CompteApi));
        } else {
            //Sinon, chargement des données du compte API
            this.apiRestService.loadCompteApi(idCompte).subscribe(result => this.handleCompteApi(result));
        }
    }

    /**
     * Traitement des données du compte API
     *
     * @param result résultat contenant les données à charger
     * @param successMessage message de succès à afficher
     */
    private handleCompteApi(result: Result,successMessage?: string): void {
        if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
            //Chargement des données du compte API
            this._compteApi.next(new CompteApi(result.data.compteApi));

            //Toast de succès si paramètre fourni
            if (successMessage) { this.toastrService.success(this.translateService.instant(successMessage)); }
        } else if (Number(result.codeErreur) === ApiRestDetailsService.ERROR_CLEF) {
            //Message d'erreur spécifique pour la clé
            this.toastrService.error(this.translateService.instant('admin.parametres.api.rest.details.errors.clef'));
        } else {
            //Message d'erreur
            TypeCodeErreur.showError(result.codeErreur as TypeCodeErreur, this.translateService, this.toastrService);
        }
    }

    /**
     * Sauvegarde du compte API
     */
    saveCompteApi(): void {
        //Si le compte est personnel, on le force à false
        this.compteApi.comptePersonnel = false;

        //Sauvegarde du compte API
        this.apiRestService.saveCompteApi(this.compteApi).subscribe({
            next: (result: Result) => this.handleCompteApi(result,'global.success.enregistrement'),
            error: (error: any) => TypeCodeErreur.showError(TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService)
        });
    }

    /**
     * Suppression d'un compte API
     */
    deleteCompteApi(): void {
        //Popup de confirmation
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed)).subscribe({
            next: () => {
                //Suppression du compte API
                this.apiRestService.deleteCompteApi(this.compteApi.idCompte).subscribe({
                    next: (result: Result) => {
                        //Confirmation de succès
                        this.toastrService.success(this.translateService.instant('global.success.suppression'));

                        //Redirection vers la liste
                        this.router.navigate(['Admin/Parametre/API/Rest']);
                    },
                    error: (error: any) => TypeCodeErreur.showError(TypeCodeErreur.ERROR_DELETE, this.translateService, this.toastrService)
                });
            }
        });
    }

    /**
     * Générer une nouvelle clé pour le compte
     */
    generateNewKey(): void {
        //Générer une nouvelle clé pour le compte
        this.apiRestService.generateNewKey(this.compteApi.idCompte).subscribe({
            next: (result: Result) => this.handleCompteApi(result,'global.success.generic'),
            error: (error: any) => TypeCodeErreur.showError(TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService)
        });
    }

    /**
     * Déverrouiller un compte API
     */
    unlockCompteApi(): void {
        //Déverrouiller un compte API
        this.apiRestService.unlockCompteApi(this.compteApi.idCompte).subscribe({
            next: (result: Result) => this.handleCompteApi(result,'global.success.generic'),
            error: (error: any) => TypeCodeErreur.showError(TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService)
        });
    }

    /**
     * Chargement des informations sur l'API
     */
    loadApiInformation(): void {
        //Vérification pour ne charger les informations sur l'API qu'une seule fois
        if (this.apiInformation != null) {
            //Si les informations sur l'API sont déjà chargées, on les retourne
            this._apiInformation.next(this.apiInformation);
        } else {
            //Chargement des informations sur l'API
            this.apiRestService.loadApiInformation().subscribe({
                next: (apiInformation: ApiInformation) => this._apiInformation.next(apiInformation),
                error: (error: any) => TypeCodeErreur.showError(TypeCodeErreur.ERROR_LOAD, this.translateService, this.toastrService)
            });
        }
    }
}